import { Link } from 'react-router-dom'
import { Calendar, Package, Bus, ShoppingCart, AlertCircle, Download, FileText, Lock, Smartphone, Plus } from 'lucide-react'
import { useTestspielStore } from '../stores/testspielStore'
import { useSupabaseOrderStore } from '../stores/supabaseOrderStore'
import { useBusStore } from '../stores/busStore'
import { useFileManagementStore } from '../stores/fileManagementStore'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { useState, useEffect } from 'react'

export default function HomePage() {
  const testspiele = useTestspielStore(state => state.testspiele)
  const { orders, fetchOrders } = useSupabaseOrderStore()
  const busTrips = useBusStore(state => state.busTrips)
  
  // File management
  const { files, fetchFiles, downloadFile } = useFileManagementStore()
  const [showFileDownload, setShowFileDownload] = useState(false)
  const [downloadCode, setDownloadCode] = useState('')
  const [codeError, setCodeError] = useState('')

  // PWA Installation
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [showInstallPrompt, setShowInstallPrompt] = useState(false)

  // Fetch files on component mount
  useEffect(() => {
    fetchFiles()
    fetchOrders() // Fetch Supabase orders
  }, [fetchFiles, fetchOrders])

  // Calculate pending orders count from Supabase
  const pendingOrdersCount = orders.filter(order => order.status === 'pending').length

  // PWA Installation logic
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Save the event so it can be triggered later
      setDeferredPrompt(e)
      // Show install prompt if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallPrompt(true)
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallPrompt(false)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    // Show the install prompt
    deferredPrompt.prompt()

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt')
    } else {
      console.log('User dismissed the install prompt')
    }

    // Clear the deferredPrompt variable
    setDeferredPrompt(null)
    setShowInstallPrompt(false)
  }

  // Download access code
  const DOWNLOAD_CODE = '5070'

  const handleCodeSubmit = () => {
    if (downloadCode === DOWNLOAD_CODE) {
      setShowFileDownload(true)
      setCodeError('')
    } else {
      setCodeError('Falscher Code! Bitte versuchen Sie es erneut.')
      setDownloadCode('')
    }
  }

  const handleDownload = async (fileId: string) => {
    const result = await downloadFile(fileId)
    if (result.error) {
      alert('Fehler beim Download: ' + result.error)
    }
  }

  // Get next upcoming testspiel
  const upcomingTestspiele = testspiele
    .filter(t => new Date(t.date) > new Date())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  
  const nextTestspiel = upcomingTestspiele[0]

  // Get upcoming bus trips
  const upcomingBusTrips = busTrips
    .filter(trip => new Date(trip.date) > new Date() && trip.status === 'open')
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 2)

  const quickActions = [
    {
      title: 'Testspiele',
      description: 'Verwalte Testspiele und Trainingseinheiten',
      icon: Calendar,
      link: '/testspiele',
      count: upcomingTestspiele.length
    },
    {
      title: 'Equipment & Bestellungen',
      description: 'Bestelle Vereinsausrüstung und verwalte Bestellungen',
      icon: Package,
      link: '/equipment',
      count: pendingOrdersCount
    },
    {
      title: 'Bus',
      description: 'Reserviere den Vereinsbus für Fahrten',
      icon: Bus,
      link: '/bus',
      count: upcomingBusTrips.length
    }
  ]

  return (
    <div style={{ padding: '20px 0' }}>
      {/* PWA Install Prompt */}
      {showInstallPrompt && (
        <div style={{
          background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Smartphone size={24} />
            <div>
              <h4 style={{
                margin: '0 0 4px 0',
                fontSize: '16px',
                fontWeight: '700'
              }}>
                FC Frick App installieren
              </h4>
              <p style={{
                margin: '0',
                fontSize: '14px',
                opacity: 0.9
              }}>
                Installiere die App für schnelleren Zugriff und Offline-Nutzung
              </p>
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            <button
              onClick={() => setShowInstallPrompt(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Später
            </button>
            <button
              onClick={handleInstallClick}
              style={{
                background: 'white',
                color: '#1B7E3A',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Plus size={12} />
              Installieren
            </button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '800',
          background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '8px',
          letterSpacing: '-0.025em'
        }}>
          Willkommen bei FC Frick
        </h1>
        <p style={{ 
          color: '#6b7280', 
          fontSize: '18px',
          fontWeight: '500'
        }}>
          Verwalte Testspiele, bestelle Equipment und reserviere den Bus
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {quickActions.map((action) => {
          const Icon = action.icon
          return (
            <Link
              key={action.title}
              to={action.link}
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e5e7eb',
                padding: '24px',
                textDecoration: 'none', 
                color: 'inherit', 
                transition: 'all 0.3s ease',
                display: 'block',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              {/* Decorative gradient overlay */}
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
                borderRadius: '16px 16px 0 0'
              }} />
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div style={{
                    padding: '12px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(27, 126, 58, 0.3)'
                  }}>
                    <Icon size={24} />
                  </div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#111827',
                    margin: '0',
                    letterSpacing: '-0.025em'
                  }}>
                    {action.title}
                  </h3>
                </div>
                {action.count !== undefined && action.count > 0 && (
                  <span style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '700',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    minWidth: '24px',
                    textAlign: 'center',
                    boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                  }}>
                    {action.count}
                  </span>
                )}
              </div>
              <p style={{
                color: '#6B7280',
                fontSize: '14px',
                fontWeight: '500',
                margin: '0',
                lineHeight: '1.5'
              }}>
                {action.description}
              </p>
            </Link>
          )
        })}
      </div>

      {/* File Download Section */}
      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative gradient overlay */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '4px',
          background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 50%, #3B82F6 100%)',
          borderRadius: '16px 16px 0 0'
        }} />

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            padding: '8px',
            background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Download size={20} style={{ color: '#3B82F6' }} />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#111827',
            margin: '0'
          }}>
            Vereinsdokumente
          </h3>
        </div>

        {!showFileDownload ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <Lock size={16} style={{ color: '#6B7280' }} />
              <p style={{
                color: '#6B7280',
                fontSize: '14px',
                margin: '0',
                fontWeight: '500'
              }}>
                Code eingeben um Dokumente herunterzuladen
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              <input
                type="password"
                value={downloadCode}
                onChange={(e) => setDownloadCode(e.target.value)}
                placeholder="Code eingeben"
                style={{
                  padding: '8px 12px',
                  border: codeError ? '2px solid #EF4444' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  width: '120px',
                  textAlign: 'center'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCodeSubmit()
                  }
                }}
              />
              <button
                onClick={handleCodeSubmit}
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                Öffnen
              </button>
            </div>
            
            {codeError && (
              <p style={{
                color: '#EF4444',
                fontSize: '12px',
                margin: '0',
                fontWeight: '500'
              }}>
                {codeError}
              </p>
            )}
          </div>
        ) : (
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {files.length === 0 ? (
              <p style={{
                color: '#6B7280',
                fontSize: '14px',
                textAlign: 'center',
                margin: '20px 0',
                fontWeight: '500'
              }}>
                Keine Dokumente verfügbar
              </p>
            ) : (
              <div style={{
                display: 'grid',
                gap: '8px'
              }}>
                {files.map((file) => (
                  <div
                    key={file.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9'
                      e.currentTarget.style.borderColor = '#cbd5e1'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8fafc'
                      e.currentTarget.style.borderColor = '#e5e7eb'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flex: 1
                    }}>
                      <FileText size={16} style={{ color: '#6B7280' }} />
                      <div>
                        <p style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#111827',
                          margin: '0 0 2px 0'
                        }}>
                          {file.originalName}
                        </p>
                        <p style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          margin: '0'
                        }}>
                          {file.category} • {(file.fileSize / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(file.id)}
                      style={{
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)'
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    >
                      <Download size={12} />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient overlay */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '4px',
            background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
            borderRadius: '16px 16px 0 0'
          }} />
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              padding: '8px',
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Calendar size={20} style={{ color: '#16A34A' }} />
            </div>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#111827',
              margin: '0'
            }}>
              Nächstes Testspiel
            </h3>
          </div>
          {nextTestspiel ? (
            <div>
              <p style={{
                fontWeight: '600',
                marginBottom: '4px',
                color: '#1F2937',
                fontSize: '14px'
              }}>
                {nextTestspiel.opponent}
              </p>
              <p style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                {format(new Date(nextTestspiel.date), 'EEEE, d. MMMM yyyy', { locale: de })}
              </p>
              <p style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                {nextTestspiel.time} Uhr
              </p>
              <p style={{
                fontSize: '12px',
                color: '#6B7280',
                fontWeight: '500'
              }}>
                {nextTestspiel.team}
              </p>
            </div>
          ) : (
            <p style={{
              color: '#6B7280',
              fontSize: '14px',
              fontWeight: '500',
              margin: '0'
            }}>
              Keine bevorstehenden Testspiele
            </p>
          )}
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient overlay */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '4px',
            background: 'linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%)',
            borderRadius: '16px 16px 0 0'
          }} />
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              padding: '8px',
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ShoppingCart size={20} style={{ color: '#DC2626' }} />
            </div>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#111827',
              margin: '0'
            }}>
              Bestellungen
            </h3>
          </div>
          <div>
            <p style={{
              fontWeight: '600',
              marginBottom: '8px',
              color: '#1F2937',
              fontSize: '14px'
            }}>
              {pendingOrdersCount} offene Bestellungen
            </p>
            {pendingOrdersCount > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#ea580c'
              }}>
                <AlertCircle size={16} />
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  Genehmigung erforderlich
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient overlay */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '4px',
            background: 'linear-gradient(90deg, #2563eb 0%, #3b82f6 50%, #2563eb 100%)',
            borderRadius: '16px 16px 0 0'
          }} />
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              padding: '8px',
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Bus size={20} style={{ color: '#2563EB' }} />
            </div>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#111827',
              margin: '0'
            }}>
              Bus verfügbar
            </h3>
          </div>
          {upcomingBusTrips.length > 0 ? (
            <div>
              <p style={{
                fontWeight: '600',
                marginBottom: '8px',
                color: '#1F2937',
                fontSize: '14px'
              }}>
                Nächste Fahrten:
              </p>
              {upcomingBusTrips.map(trip => (
                <p key={trip.id} style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  marginBottom: '4px',
                  fontWeight: '500'
                }}>
                  {format(new Date(trip.date), 'd. MMM', { locale: de })} - {trip.destination}
                </p>
              ))}
            </div>
          ) : (
            <p style={{
              color: '#6B7280',
              fontSize: '14px',
              fontWeight: '500',
              margin: '0'
            }}>
              Keine bevorstehenden Busfahrten
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
